import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { ManualGoalStep } from '@/features/campaigns/components/ManualGoalStep';
import { ScreensStep } from '@/features/campaigns/components/ScreensStep';
import { ScheduleStepPanel } from '@/features/campaigns/components/ScheduleStep/ScheduleStepPanel';
import { PreviewStep } from '@/features/campaigns/components/PreviewStep';
import { AIBuilder } from '@/features/campaigns/components/AIBuilder';
import { AIPreview } from '@/features/campaigns/components/AIPreview';
import { SessionTimer } from '@/features/campaigns/components/SessionTimer';
import { CampaignSuccessModal } from '@/features/campaigns/components/CampaignSuccessModal';
import { useSlotHold } from '@/features/campaigns/hooks/useSlotHold';
import {
  ManualAlertProvider,
  ManualBuilderLayout,
} from '@/features/campaigns/components/ManualBuilder';
import { useCampaignCreatePage } from '@/features/campaigns/hooks/useCampaignCreatePage';
import { AI_SESSION_SECONDS } from '@/features/campaigns/constants/campaignOptions';
import { isBillboardCreateLaunch } from '@/features/campaigns/types/billboardCreateLaunch';
import { DepositModal } from '@/features/wallet/components/DepositModal';
import { formatMoney } from '@/shared/lib/format/money';
import { CampaignBusyOverlay } from '@/features/campaigns/components/CampaignBusyOverlay';
import {
  ScheduleSummaryProvider,
  useScheduleSummary,
} from '@/features/campaigns/components/ScheduleStep/scheduleSummaryContext';
import { ScheduleSummaryDrawer } from '@/features/campaigns/components/ScheduleStep/sections/ScheduleSummaryDrawer';

/** Remount when starting a new campaign so previous city/dates cannot stick. */
export function CampaignCreatePage() {
  const location = useLocation();
  const state = location.state as
    | { freshId?: number; billboardId?: string; from?: string }
    | null;
  const remountKey = isBillboardCreateLaunch(state)
    ? `billboard-${state.billboardId}`
    : state?.freshId
      ? `fresh-${state.freshId}`
      : 'resume';

  return (
    <ManualAlertProvider>
      <ScheduleSummaryProvider>
        <CampaignCreatePageInner key={remountKey} />
      </ScheduleSummaryProvider>
    </ManualAlertProvider>
  );
}

function CampaignCreatePageInner() {
  const page = useCampaignCreatePage();
  const scheduleSummary = useScheduleSummary();

  // The timer shows the server's hold on the slots, not a clock of its own.
  const holdCampaignId =
    page.builderMode === 'ai'
      ? page.aiGeneratedData?.campaignId
      : page.currentStep >= 3
        ? page.submitData?.campaignId
        : undefined;
  const hold = useSlotHold(holdCampaignId, page.aiGeneratedData ?? page.previewData, page.builderMode === 'ai');
  const { handleSessionExpiry } = page;
  useEffect(() => {
    if (hold.lost) handleSessionExpiry();
  }, [hold.lost, handleSessionExpiry]);

  if (!page.builderMode) {
    return <Navigate to="/campaigns" replace />;
  }

  const step4Payable = Number(
    page.previewData?.pricing?.totalAmount ??
      page.previewData?.pricing?.amountWithTax ??
      0,
  );
  const nextLabel =
    page.currentStep === 4
      ? step4Payable > 0
        ? `Pay ${formatMoney(step4Payable)} & Launch`
        : 'Pay & Launch'
      : page.currentStep === 3
        ? 'Review & pay'
        : page.currentStep === 2
          ? 'Continue to slots'
          : page.skipScreenStep
            ? 'Continue to slots'
            : 'Continue to Select Screens';

  const nextDisabledByDetection =
    page.builderMode === 'standard' &&
    page.currentStep === 1 &&
    page.detectingOrientation;

  const busyTitle =
    page.currentStep === 4 ? 'Launching campaign…' : 'Saving…';
  const busyMessage =
    page.currentStep === 4
      ? 'Confirming payment and creating your campaign. Please don’t close this page.'
      : 'Saving your progress. Please don’t close this page.';

  const stickyHint =
    page.currentStep === 1
      ? page.detectingOrientation
        ? 'Wait for orientation analysis before continuing.'
        : page.skipScreenStep
          ? 'Screen is already selected from the billboard page.'
          : 'Complete required fields, then continue to select screens.'
      : page.currentStep === 2
        ? `${page.stepTwoData.billboardIds.length} of 10 screens selected.`
        : page.currentStep === 3
          ? page.slotsHoldExpired
            ? 'Reservation expired — select time slots again to continue.'
            : page.submitData.bookingSlots.length === 0
              ? 'Open schedule workspace to book hours.'
              : page.repetitionType === 'screen_wise'
                ? `${page.submitData.bookingSlots.length} slot${page.submitData.bookingSlots.length === 1 ? '' : 's'} · per screen`
                : `${page.submitData.bookingSlots.length} time slot${page.submitData.bookingSlots.length === 1 ? '' : 's'} · same for all`
          : page.belowMinOrder
            ? 'Order below INR 500 — go back and increase slots to continue.'
            : page.exceedsMaxOrder
              ? 'Order exceeds INR 1,00,000 — reduce screens, dates, or slots.'
              : page.needsTopUp
                ? `Wallet short · due ${formatMoney(step4Payable)} — pay once to top up & launch.`
                : step4Payable > 0
                  ? `Due ${formatMoney(step4Payable)} · wallet ready for instant launch.`
                  : 'Confirm pricing and wallet balance before launch.';

  const bookedSlots = page.submitData.bookingSlots.length;

  const stickyBar = (
    <div className="camp-sticky-bar">
      <div className="camp-sticky-bar__inner">
        {page.currentStep === 3 ? (
          <div className="hidden min-w-0 flex-1 items-center gap-2.5 sm:flex">
            <p className="m-0 text-left text-xs leading-relaxed text-[var(--dash-muted)]">
              {page.slotsHoldExpired ? (
                'Reservation expired — select time slots again.'
              ) : bookedSlots === 0 ? (
                'Open the schedule workspace to book hours — then Review & pay.'
              ) : page.errors.screenSlots ? (
                page.errors.screenSlots
              ) : (
                <>
                  <span className="font-semibold tabular-nums text-[var(--dash-ink)]">
                    {bookedSlots}
                  </span>{' '}
                  slot{bookedSlots === 1 ? '' : 's'} booked.
                </>
              )}
            </p>
            <button
              type="button"
              onClick={scheduleSummary.openSummary}
              className={`shrink-0 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition ${
                bookedSlots > 0
                  ? 'bg-[var(--dash-brand)] text-white hover:brightness-105'
                  : 'border border-[var(--dash-line)] bg-white text-[var(--dash-ink)] hover:bg-[var(--dash-soft)]'
              }`}
            >
              {bookedSlots > 0
                ? `View schedule · ${bookedSlots}`
                : 'View schedule'}
            </button>
          </div>
        ) : (
          <p className="m-0 hidden text-left text-xs leading-relaxed text-[var(--dash-muted)] sm:block sm:max-w-[48%]">
            {stickyHint}
          </p>
        )}
        <div className="camp-sticky-bar__actions">
          {page.currentStep === 3 ? (
            <button
              type="button"
              onClick={scheduleSummary.openSummary}
              className={`sm:hidden !min-w-0 !px-2.5 ${
                bookedSlots > 0
                  ? 'dash-btn-gradient camp-sticky-bar__secondary !border-0 !text-white'
                  : 'camp-sticky-bar__secondary'
              }`}
              aria-label={
                bookedSlots > 0
                  ? `View schedule, ${bookedSlots} slots`
                  : 'View schedule'
              }
            >
              {bookedSlots > 0 ? bookedSlots : 'Plan'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={page.handleBack}
            className="camp-sticky-bar__secondary !min-w-0 !px-3 sm:!px-4"
          >
            {page.currentStep === 1 ? 'Cancel' : 'Back'}
          </button>
          <button
            type="button"
            disabled={
              page.isBusy ||
              nextDisabledByDetection ||
              (page.currentStep === 4 &&
                (page.belowMinOrder || page.exceedsMaxOrder))
            }
            onClick={page.handleNext}
            className="dash-btn-gradient camp-sticky-bar__primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {page.isBusy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {page.currentStep === 4 ? 'Processing…' : 'Saving…'}
              </>
            ) : (
              <>
                <span className="sm:hidden">
                  {page.currentStep === 3
                    ? 'Review'
                    : page.currentStep === 4
                      ? step4Payable > 0
                        ? `Pay ${formatMoney(step4Payable)}`
                        : 'Pay & Launch'
                      : nextLabel}
                </span>
                <span className="hidden sm:inline">{nextLabel}</span>
                {page.currentStep < 4 ? (
                  <ArrowRight className="h-4 w-4 shrink-0 opacity-90" />
                ) : null}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const topBreadcrumb = (
    <div className="flex shrink-0 items-center justify-between gap-3 pb-1">
      <button
        type="button"
        onClick={() => {
          if (page.currentStep > 1) {
            page.handleBack();
            return;
          }
          page.navigate('/campaigns');
        }}
        className="inline-flex items-center gap-1 rounded-lg px-1 py-0.5 text-[12px] font-medium text-[var(--dash-muted)] transition hover:bg-white hover:text-[var(--dash-ink)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {page.currentStep > 1
          ? 'Previous step'
          : page.skipScreenStep
            ? 'Back to billboard'
            : 'All campaigns'}
      </button>

      <div className="flex items-center gap-2">
        {page.showTimer ? (
          <SessionTimer
            key={page.timerKey}
            initialSeconds={AI_SESSION_SECONDS}
            onExpire={page.handleSessionExpiry}
            inline={true}
          />
        ) : null}

        <span className="rounded-md bg-white px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase ring-1 ring-[var(--dash-line)]">
          Manual Campaign
        </span>
      </div>
    </div>
  );

  return (
    <div
      ref={page.topRef}
      className="mx-auto flex h-full min-h-0 w-full max-w-[1600px] flex-1 flex-col overflow-hidden"
    >
      {page.showTimer && page.builderMode === 'ai' ? (
        <SessionTimer
          key={page.timerKey}
          initialSeconds={hold.secondsLeft ?? AI_SESSION_SECONDS}
          totalSeconds={AI_SESSION_SECONDS}
          onExpire={page.handleSessionExpiry}
        />
      ) : null}

      {page.builderMode === 'ai' ? (
        <div className="mb-2 flex shrink-0 items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (page.aiGeneratedData) {
                page.setAiGeneratedData(null);
                return;
              }
              page.navigate('/campaigns');
            }}
            className="inline-flex items-center gap-1 rounded-lg px-1 py-0.5 text-[12px] font-medium text-[var(--dash-muted)] transition hover:bg-white hover:text-[var(--dash-ink)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {page.aiGeneratedData ? 'Edit brief' : 'All campaigns'}
          </button>
          <span className="rounded-md bg-white px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase ring-1 ring-[var(--dash-line)]">
            AI Campaign
          </span>
        </div>
      ) : null}

      <div
        className={`camp-fade-up flex flex-1 min-h-0 flex-col ${
          page.builderMode === 'ai' ? 'overflow-y-auto overscroll-contain' : 'overflow-hidden'
        }`}
      >
        {page.builderMode === 'ai' && page.currentStep === 1 ? (
          <>
            <div className={page.aiGeneratedData ? 'hidden' : 'block'}>
              <AIBuilder
                formData={page.formData}
                setFormData={page.setFormData}
                onPreviewGenerated={(data) => page.setAiGeneratedData(data)}
              />
            </div>
            {page.aiGeneratedData ? (
              <AIPreview
                aiData={page.aiGeneratedData}
                formData={page.formData}
                onAiDataChange={(data) => page.setAiGeneratedData(data)}
                onCampaignCompleted={() => page.setIsCampaignCompleted(true)}
                onLaunchSuccess={(id) => {
                  page.setIsCampaignCompleted(true);
                  page.onAiLaunchSuccess(id);
                }}
              />
            ) : null}
          </>
        ) : (
          <ManualBuilderLayout
            currentStep={page.currentStep}
            skipScreenStep={page.skipScreenStep}
            maxStepReached={page.maxStepReached}
            onStepSelect={page.goToStep}
            stepNavDisabled={page.isBusy}
            topBreadcrumb={topBreadcrumb}
            campaignData={page.campaignData}
            setCampaignData={page.setCampaignData}
            creativePreview={page.creativePreview}
            setCreativePreview={page.setCreativePreview}
            errors={page.errors}
            clearError={page.clearError}
            selectedScreensMeta={page.selectedScreensMeta}
            previewData={page.previewData}
            availableBalance={page.availableBalance}
            detectingOrientation={page.detectingOrientation}
            onDetectingChange={page.setDetectingOrientation}
            selectionSource={page.screenSelectionSource}
            stickyBar={stickyBar}
          >
            {page.currentStep === 1 ? (
              <ManualGoalStep
                campaignData={page.campaignData}
                setCampaignData={page.setCampaignData}
                creativePreview={page.creativePreview}
                setCreativePreview={page.setCreativePreview}
                errors={page.errors}
                clearError={page.clearError}
                allowToday={page.skipScreenStep}
                hideWhereWhen={page.skipScreenStep}
                onDetectingChange={page.setDetectingOrientation}
                onContinueToNextStep={page.handleNext}
                continueLabel={
                  page.skipScreenStep
                    ? 'Continue to slots'
                    : 'Continue to Select Screens'
                }
              />
            ) : null}

            {page.currentStep === 2 && !page.skipScreenStep ? (
              <ScreensStep
                campaignData={page.campaignData}
                selectedIds={page.stepTwoData.billboardIds}
                selectedMeta={page.selectedScreensMeta}
                onSelect={page.handleSelectScreen}
                onClearAll={page.handleClearAllScreens}
                error={page.errors.billboardIds}
                selectionSource={page.screenSelectionSource}
              />
            ) : null}

            {page.currentStep === 3 ? (
              <ScheduleStepPanel
                repetitionType={page.repetitionType}
                setRepetitionType={page.setRepetitionType}
                hasBookedSlots={page.submitData.bookingSlots.length > 0}
                onResetSlots={page.resetBookingSlots}
                showModePicker={page.stepTwoData.billboardIds.length >= 2}
                billboardIds={page.stepTwoData.billboardIds}
                campaignData={page.campaignData}
                submitData={page.submitData}
                setSubmitData={page.setSubmitData}
                slotsHoldExpired={page.slotsHoldExpired}
                slotsSessionKey={page.slotsSessionKey}
                selectedMeta={page.selectedScreensMeta}
                onBackToScreens={page.handleBack}
                screenSlotsError={page.errors.screenSlots}
                onClearScreenSlotsError={() => page.clearError('screenSlots')}
              />
            ) : null}

            {page.currentStep === 4 ? (
              <PreviewStep
                data={page.previewData}
                campaignData={page.campaignData}
                creativePreview={page.creativePreview}
                availableBalance={page.availableBalance}
                selectedScreensMeta={page.selectedScreensMeta}
                altMediaIds={page.altMediaIds}
                setAltMediaIds={page.setAltMediaIds}
                onLaunch={page.handleNext}
                isLaunching={page.isBusy}
                onBack={page.handleBack}
                coupon={page.coupon}
                bookingSlotCount={page.submitData.bookingSlots.length}
              />
            ) : null}
          </ManualBuilderLayout>
        )}
      </div>

      <CampaignBusyOverlay
        open={page.isBusy}
        title={busyTitle}
        message={busyMessage}
      />

      <DepositModal
        open={page.depositOpen}
        onClose={page.closeDeposit}
        availableBalance={page.availableBalance}
        initialAmount={page.depositAmount}
        minAmount={page.depositAmount}
        title="Add funds to launch"
        subtitle={`Need ${formatMoney(page.launchShortfall)} more. Pay with Razorpay, then we launch automatically.`}
        confirmLabel="Pay with Razorpay"
        onSuccess={page.handleDepositSuccess}
      />

      <CampaignSuccessModal
        open={page.showSuccess}
        campaignId={page.successCampaignId}
        onDone={() => page.navigate('/campaigns')}
      />

      <ScheduleSummaryDrawer />
    </div>
  );
}
